/** Share the SDK's send button between structured selections and ordinary messages. */
export function createBookingComposer(form: HTMLFormElement, closeMenus: () => void) {
  let input: HTMLTextAreaElement | undefined;
  let shell: HTMLElement | null = null;
  let send: HTMLButtonElement | null = null;
  let textMode = false;
  let textModeEnabled = true;
  let selection = '';
  let draft = '';
  let syncingInput = false;
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'otokoc-picker-mode';

  const writeInput = (value: string) => {
    if (!input || input.value === value) return;
    syncingInput = true;
    try {
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    } finally {
      syncingInput = false;
    }
  };
  const render = () => {
    if (!shell) return;
    const mode = textMode ? 'text' : 'select';
    if (shell.dataset.otokocPickerMode !== mode) shell.dataset.otokocPickerMode = mode;
    toggle.hidden = !textModeEnabled;
    if (input) {
      input.hidden = !textModeEnabled && !textMode;
      input.style.display = !textModeEnabled && !textMode ? 'none' : '';
    }
    const label = textMode ? 'Listeden seç' : 'Mesaj yaz';
    if (toggle.getAttribute('aria-label') !== label) {
      toggle.setAttribute('aria-label', label);
      toggle.title = label;
      toggle.innerHTML = textMode
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M8 6h12M8 12h12M8 18h12M3 6h1M3 12h1M3 18h1"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m15 4 5 5M4 20l5-1L20 8a2 2 0 0 0-5-5L4 14z"/></svg>';
    }
  };
  const showText = (seed?: string) => {
    if (!textModeEnabled) return;
    closeMenus();
    textMode = true;
    if (seed !== undefined) draft = seed;
    writeInput(draft);
    render();
    input?.focus();
  };
  toggle.addEventListener('click', () => {
    if (!textModeEnabled) return;
    if (!textMode) return showText();
    draft = input?.value ?? '';
    textMode = false;
    writeInput(selection);
    render();
    form.querySelector<HTMLElement>('input, [role="combobox"]')?.focus();
  });
  const activateTextMode = () => {
    if (!input || textMode || !textModeEnabled) return;
    closeMenus();
    textMode = true;
    if (input.value !== selection) draft = input.value;
    writeInput(draft);
    render();
  };
  const activateSelectMode = () => {
    if (!input || !textMode) return;
    closeMenus();
    draft = input.value;
    textMode = false;
    writeInput(selection);
    render();
  };
  const captureDraft = () => {
    if (syncingInput || !input || !textModeEnabled) return;
    draft = input.value;
    if (!textMode) {
      closeMenus();
      textMode = true;
      render();
    }
  };
  const isInputFocused = () => {
    if (!input) return false;
    const root = input.getRootNode();
    return (
      document.activeElement === input ||
      (root instanceof ShadowRoot && root.activeElement === input) ||
      input.matches(':focus')
    );
  };
  const submitSelection = (event: Event) => {
    if (textMode || send?.classList.contains('gengage-chat-send--stop')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.requestSubmit();
  };
  form.addEventListener('pointerdown', activateSelectMode, true);
  form.addEventListener('focusin', activateSelectMode, true);
  const detach = () => {
    if (shell) {
      send?.removeEventListener('click', submitSelection, true);
      input?.removeEventListener('focus', activateTextMode, true);
      input?.removeEventListener('input', captureDraft, true);
      if (!textMode) writeInput(draft);
      if (input) {
        input.hidden = false;
        input.style.display = '';
      }
      delete shell.dataset.otokocPickerMode;
    }
    form.remove();
    toggle.remove();
    input = undefined;
    shell = null;
    send = null;
  };
  return {
    showText,
    detach,
    clearDraft() {
      draft = '';
      writeInput('');
    },
    setSelection(value: string) {
      selection = value;
      if (!textMode) writeInput(value);
    },
    setTextModeEnabled(enabled: boolean) {
      textModeEnabled = enabled;
      if (!enabled && textMode) {
        closeMenus();
        textMode = false;
        writeInput(selection);
      }
      render();
    },
    attach(nextInput: HTMLTextAreaElement) {
      const nextShell = nextInput.closest<HTMLElement>('[data-gengage-part="chat-input-shell"]');
      if (!nextShell) return;
      if (shell !== nextShell) {
        detach();
        input = nextInput;
        shell = nextShell;
        send = shell.querySelector<HTMLButtonElement>('[data-gengage-part="chat-send"]');
        draft = input.value;
        textMode = !!draft.trim();
        shell.insertBefore(form, input);
        if (send) shell.insertBefore(toggle, send);
        send?.addEventListener('click', submitSelection, true);
        input.addEventListener('focus', activateTextMode, true);
        input.addEventListener('input', captureDraft, true);
      }
      if (!textModeEnabled) textMode = false;
      if (!textMode && isInputFocused()) nextInput.blur();
      if (!textMode) writeInput(selection);
      render();
    },
  };
}
